import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  InputAdornment,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { HelpOutline as HelpIcon, LoginOutlined as LoginIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Role } from '../../types';
import DebugInfo from '../debug/DebugInfo';
import WhatsAppButton from '../common/WhatsAppButton';
import { ApiError } from '../../services/api/http';
import AuthPageShell from './AuthPageShell';
import { createTextActionSx } from '../../theme/interactions';

interface LocationState {
  from: {
    pathname: string;
  };
  message?: string;
}

export const isAuthorizedRedirect = (pathname: string, role: Role): boolean => {
  const section = pathname.split('/').filter(Boolean)[0];

  // These routes are available to every authenticated role.
  if (!section || section === 'dashboard' || section === 'profile'
      || section === 'reports' || section === 'customers' || section === 'items'
      || section === 'sales' || section === 'debts') {
    return true;
  }

  if (section === 'companies') return role === Role.SUPER_ADMIN;
  if (section === 'users') return role === Role.SUPER_ADMIN || role === Role.COMPANY_ADMIN;

  return false;
};

const Login: React.FC = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ usernameOrEmail?: string; password?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as LocationState)?.from?.pathname || '/dashboard';

  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.message) {
      setSuccessMessage(state.message);
    }
  }, [location.state]);

  // Clear error when user types in any field (only when they change, not when error changes)
  useEffect(() => {
    if (error && (usernameOrEmail.length > 0 || password.length > 0)) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameOrEmail, password]);

  const validateForm = (): boolean => {
    const errors: { usernameOrEmail?: string; password?: string } = {};

    if (!usernameOrEmail.trim()) {
      errors.usernameOrEmail = t('auth.fieldRequired');
    }
    if (!password) {
      errors.password = t('auth.fieldRequired');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const authenticatedUser = await login(usernameOrEmail, password);
      const targetPath = isAuthorizedRedirect(from, authenticatedUser.role) ? from : '/dashboard';
      navigate(targetPath, { replace: true });
    } catch (err: unknown) {
      // Map backend error messages to translated versions using HTTP status
      const status = err instanceof ApiError ? err.status : undefined;
      let errorMessage: string;
      
      if (status === 401) {
        // Backend returns 'Invalid credentials' for wrong password/username
        errorMessage = t('auth.invalidCredentials');
      } else if (status === 403) {
        // Email not verified
        errorMessage = t('auth.emailNotVerified');
      } else if (status === 404) {
        errorMessage = t('auth.userNotFound');
      } else if (!status) {
        // Network error (no response from server)
        errorMessage = t('auth.serverConnectionError');
      } else {
        // Fallback: use backend's message if available
        errorMessage = err instanceof Error && err.message ? err.message : t('auth.loginFailed');
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AuthPageShell title={t('auth.login')} description={t('auth.signInDescription')} icon={<LoginIcon />}>
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'grid', gap: 2 }}>
          <TextField fullWidth id="usernameOrEmail" label={`${t('auth.username')} / ${t('auth.email')}`}
            name="usernameOrEmail" autoComplete="username" autoFocus value={usernameOrEmail}
            onChange={event => setUsernameOrEmail(event.target.value)} disabled={isLoading}
            error={!!fieldErrors.usernameOrEmail}
            helperText={fieldErrors.usernameOrEmail || t('auth.usernameOrEmailHelper')} />
          <TextField fullWidth name="password" label={t('auth.password')} type={showPassword ? 'text' : 'password'}
            id="password" autoComplete="current-password" value={password}
            onChange={event => setPassword(event.target.value)} disabled={isLoading}
            error={!!fieldErrors.password} helperText={fieldErrors.password}
            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              onClick={() => setShowPassword(value => !value)} edge="end">
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton></InputAdornment> }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography component={Link} to="/forgot-password" variant="body2" sx={theme => ({ color: 'primary.main', fontWeight: 600, textDecoration: 'none', ...createTextActionSx(theme) })}>
              {t('auth.forgotPassword')}
            </Typography>
          </Box>
          <Button type="submit" data-testid="login-submit" fullWidth variant="contained" disabled={isLoading} sx={{ mt: .5 }}>
            {isLoading ? <CircularProgress size={24} /> : t('auth.loginButton')}
          </Button>
        </Box>
        <Box sx={{ mt: 3, pt: 2.5, borderTop: theme => `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.dontHaveAccount')}{' '}
            <Typography component={Link} to="/register" variant="body2" sx={theme => ({ color: 'primary.main', fontWeight: 700, textDecoration: 'none', ...createTextActionSx(theme) })}>
              {t('auth.register')}
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
            <Button component={Link} to="/help" variant="text" size="small" startIcon={<HelpIcon />}>{t('nav.help')}</Button>
          </Box>
        </Box>
        {process.env.NODE_ENV === 'development' && <DebugInfo />}
      </AuthPageShell>
      <WhatsAppButton />
    </>
  );
};

export default Login;
